/*
* PAUSE CALCULATION
*
* This module calculates how long Amira should pause,
* prior to giving a "word level" intervention
*/
import getAreaPauseBaseline from './areaPauseBaseline';
import {  masteryConditionValues } from '../constants';

export default function calculatePauseLength(state) {
  // First, get baseline pause length, based on student area
  let pause = getAreaPauseBaseline(state.studentAREA);
  let decisionContext = {
    areaPauseBaseline: JSON.parse(JSON.stringify(pause)),
  }

  // if this pause length request isn't associated with a word,
  // return the default value
  if(!state.word) {
    decisionContext.warning = "pause length request isn't associated with a valid word. This occurs if we update pause time after the last word in a phrase";
    return {
      pauseLength: pause,
      decisionContext,
    }
  }
  // If this is the last word in a sentence, decrease time
  if((state.phraseWordMetadata.length - 1) == state.wordIndex) {
    pause *= .64;
    decisionContext.lastWord = "decrease time by 25%, due to last word in phrase";
  } else if(state.wordIndex == 0) { // If this is the first word in a phrase, increase time by 30%
    pause *= 1.25;
    decisionContext.firstWord = "increase time by 25%, due to first word in phrase";
  } else { // else (if this isn’t the first or last word)
     pause = updatePauseByMasteryCondition(state, pause, decisionContext);
  }

  // SPECIAL CASE - short pause time for short word
  if(state.word.length && state.word.length < 3) {
    decisionContext.shortword = "set pause time to minimum, due to short word";
    pause = 4600;
  }

  // After modifiers, max wait time is 7.2 seconds, and min wait time is 3.25 seconds
  if(pause > 7800) {
    pause = 7800;
    decisionContext.valueCapped = "after modifiers, pause time capped at max value";
  } else if(pause < 4600) {
    pause = 4600;
    decisionContext.valueCapped = "after modifiers, pause time increased back to min value";
  }

  // return the calculated pause value
  return {
    pauseLength: pause,
    decisionContext,
  }
}

// Updates pause according to the word's mastery condition:
// LIKELY_MASTERED: decrease time by 30%
// APPROPRIATELY_CHALLENGING: increase time by 30%
// VERY_CHALLENGING: decrease time by 30%
function updatePauseByMasteryCondition(state, pause, decisionContext) {
  // Get the params that we need to calculate probability of mastery
  let word_AREA_AOA = state.word_AREA_AOA;
  let word_AREA_discrim = state.word_AREA_discrim;
  let studentAREA = state.studentAREA;

  // If we're missing any inputTypes, return false
  let ruleParams = [word_AREA_AOA, word_AREA_discrim, studentAREA];
  const hasAllInputs = Object.values(ruleParams).every(input => (input !== null));
  if(!hasAllInputs) {
    return pause;
  }

  // Calculate the logistic probability of mastery
  let probabilityOfMastery = 1 / (1 + Math.pow(Math.E,
    (-word_AREA_discrim * (studentAREA - word_AREA_AOA))
  ));

  // LIKELY_MASTERED: decrease time by 25%
  if(probabilityOfMastery > masteryConditionValues.LIKELY_MASTERED) {
    pause *= .75;
    decisionContext.masteryCondition = "decrease time by 25%, due to word being likely mastered";
  // APPROPRIATELY_CHALLENGING: increase time by 30%
  } else if(probabilityOfMastery > masteryConditionValues.ZPD_WIDE[0]) {
    if(probabilityOfMastery < masteryConditionValues.ZPD_WIDE[1]) {
      pause *= 1.25;
      decisionContext.masteryCondition = "increase time by 25%, due to word being in 'wide' ZPD (.45-.75)";
    }
  // VERY_CHALLENGING: decrease time by 30%
  } else if(probabilityOfMastery < masteryConditionValues.VERY_CHALLENGING) {
    pause *= .75;
    decisionContext.masteryCondition = "decrease time by 25%, due to word being likely mastered";
  }

  return pause;
}
